
import sanitizeHtml from 'sanitize-html';
import { Component, Vue, namespace } from 'nuxt-property-decorator';
import {
  PageDto,
  PageType,
  TranslatableField,
  EventDto,
  TextImageModel,
  ImageAlignment,
  PageScope,
  ContentDto,
  ContentType,
  JsonFileContentData,
  JsonSpeakerContentData,
} from 'fourwaves-shared';
import { NuxtError } from '@nuxt/types';
import HomeHeader from '~/components/layout/HomeHeader.vue';
import Header from '~/components/layout/Header.vue';
import EventDetails from '~/components/layout/EventDetails.vue';
import SpeakersContentBlock from '~/components/sections/SpeakersContentBlock.vue';
import LogosContentBlock from '~/components/sections/LogosContentBlock.vue';
import AccordionContentBlock from '~/components/sections/AccordionContentBlock.vue';

const EventsModule = namespace('events');

@Component({
  scrollToTop: true,
  components: {
    HomeHeader,
    Header,
    EventDetails,
    SpeakersContentBlock,
    LogosContentBlock,
    AccordionContentBlock,
  },
  validate({ params, store }) {
    // home page is always accessible
    if (params.id == null) return true;

    const page = store.state.events.pages.find(({ id }: PageDto) => id === params.id);
    if (page && page.scope === PageScope.Participants) {
      if (!store.$auth.loggedIn) {
        const error: NuxtError = new Error();
        error.statusCode = 401;
        throw error;
      } else if (!store.getters['events/isParticipant'] && !store.getters['events/isOrganizer']) {
        const error: NuxtError = new Error();
        error.statusCode = 403;
        throw error;
      }
    }
    return !!page;
  },
})
export default class Page extends Vue {
  @EventsModule.Getter currentEvent!: EventDto;
  @EventsModule.State pages!: PageDto[];

  page: PageDto | null = null;
  readonly ContentType = ContentType;
  readonly PageType = PageType;

  public head() {
    return { title: this.page && this.page.type !== PageType.Home ? this.transl8(this.page.name) : '' };
  }

  get isHomeLight(): boolean {
    // when current page is of type ContentPage
    return this.$route.name!.includes('pages');
  }

  created() {
    const page = this.$route.params.id
      ? this.pages.find(page => page.id === this.$route.params.id)
      : this.pages.find(page => page.type === PageType.Home);
    if (page) {
      this.page = page;
    }

    // msg (already_registered e.g.)
    const toastMessage = this.$route.query.tmsg;
    if (toastMessage && typeof toastMessage === 'string') {
      const query = Object.assign({}, this.$route.query);
      delete query.tmsg;
      this.$router.replace({ query });

      // show "please log in" message only when not authenticated
      if (!this.$auth.loggedIn && process.client) {
        const sanitizedMessage = sanitizeHtml(this.$t(toastMessage) as string);
        this.$toast.success(sanitizedMessage);
      }
    }
  }

  public isTitleHidden(block: ContentDto) {
    return block.type === ContentType.TextImage || block.type === ContentType.Speakers;
  }

  public hasEmptyTitle(title: TranslatableField) {
    return this.transl8(title) === '';
  }

  public getTextAndImage(data?: TranslatableField): TextImageModel {
    if (data) {
      return JSON.parse(this.transl8(data)) as TextImageModel;
    }

    return {
      fileUpload: '',
      alignment: ImageAlignment.Left,
    };
  }

  public getImages(data?: TranslatableField): JsonFileContentData {
    if (data) {
      return JSON.parse(this.transl8(data)) as JsonFileContentData;
    }

    return {
      id: '',
      originalFileName: '',
    };
  }

  public getFiles(data?: TranslatableField): JsonFileContentData[] {
    if (data && this.transl8(data)) {
      return JSON.parse(this.transl8(data)) as JsonFileContentData[];
    }

    return [];
  }

  public getSpeakers(data?: TranslatableField) {
    if (data && this.transl8(data)) {
      return JSON.parse(this.transl8(data)) as JsonSpeakerContentData[];
    }
    return [];
  }
}
